import type { PdlAffordabilityJourneyDto, PdlAffordabilityTraceStepDto } from '../api/admin';

const fmt = (n: number) =>
  new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(n);

/** Scoring table 1–8 — mirrors pdl-legacy-scoring.policy.ts assignments. */
export const SCORING_TABLE_DESCRIPTIONS: Record<number, string> = {
  1: 'Full-time job, direct deposit, non-monthly pay',
  2: 'Employed or stable benefit with direct deposit (monthly-style pay)',
  3: 'Employed without direct deposit, or part-time / self-employed',
  4: 'Irregular pay, commission, social assistance, or no direct deposit',
  5: 'Quebec-adjusted profile (province override)',
  6: 'Canada Child Benefit on the 20th with direct deposit',
  7: 'Temp agency or commission with direct deposit',
  8: 'Employment Insurance with direct deposit',
};

export const INCOME_RANGE_LABELS: Record<number, string> = {
  0: 'Up to $400/mo',
  1: '$401–$600/mo',
  2: '$601–$800/mo',
  3: '$801–$1,000/mo',
  4: '$1,001–$1,200/mo',
  5: 'Over $1,200/mo',
};

export const LOAN_CATEGORY_LABELS: Record<number, string> = {
  0: 'First-time or no prior payday loans',
  1: '1 prior payday loan (good repayment)',
  2: '2 prior loans, or slow repayment history',
  3: '3 prior loans (tier adjusted for slow pay)',
  4: '4 prior loans (tier adjusted for slow pay)',
  5: '5 prior loans (tier adjusted for slow pay)',
  6: '6+ prior payday loans',
};

export type PdlTracePhase =
  | 'profile'
  | 'pre_approval'
  | 'bank'
  | 'final'
  | 'hard_stop'
  | 'other';

const TRACE_PHASE: Record<string, PdlTracePhase> = {
  LEGACY_INCOME: 'profile',
  SCORING_TABLE: 'profile',
  INCOME_RANGE: 'profile',
  LOAN_CATEGORY: 'profile',
  GRID_RAW: 'pre_approval',
  GRID_CAPPED: 'pre_approval',
  GRID_MISS: 'pre_approval',
  MAXLOANAMT_CAP: 'pre_approval',
  EI_CAP: 'pre_approval',
  GENERAL_CAP: 'pre_approval',
  AMOUNT_DUE_REDUCE: 'pre_approval',
  LEGACY_PRA_LIMIT: 'pre_approval',
  INCOME_CENTS: 'bank',
  ELOD_UNAVAILABLE: 'bank',
  EXISTING_DEBT_CENTS: 'bank',
  INTERNAL_C4Y: 'bank',
  STACKING_DECLINE: 'bank',
  STACKING_REVIEW: 'bank',
  DEBT_NSF: 'bank',
  DEBT_RATIO: 'bank',
  LOAN_VELOCITY: 'bank',
  AVAILABLE_PAYMENT_CENTS: 'bank',
  AFFORDABILITY_MAX: 'bank',
  MAX_ELIGIBLE: 'final',
  CAPPED_TO_REQUESTED: 'final',
  BELOW_PRODUCT_MIN: 'final',
  ZERO_INCOME: 'hard_stop',
  JURISDICTION_BLOCKED: 'hard_stop',
  FEDERAL_AMOUNT_EXCEEDED: 'hard_stop',
  UNDERAGE: 'hard_stop',
  PRIOR_LOAN_PENDING: 'hard_stop',
  PRIOR_LOAN_NOT_CLEARED: 'hard_stop',
  PAST_DUE: 'hard_stop',
  BLOCKED_INCOME_TYPE: 'hard_stop',
  BLOCKED_EMPLOYER: 'hard_stop',
};

export const TRACE_PHASE_LABELS: Record<PdlTracePhase, string> = {
  profile: '1 · Customer scoring profile',
  pre_approval: '2 · Cash4You pre-approval table',
  bank: '3 · Bank payment room (40% / 50% rules)',
  final: '4 · Final offer',
  hard_stop: 'Hard stop',
  other: 'Other checks',
};

export type PdlAdjudicatorTraceRow = {
  code: string;
  phase: PdlTracePhase;
  label: string;
  detail: string;
  value: string;
};

function traceStep(
  trace: PdlAffordabilityTraceStepDto[] | undefined,
  code: string,
): PdlAffordabilityTraceStepDto | undefined {
  return trace?.find((s) => s.code === code);
}

function readTraceNumber(step?: PdlAffordabilityTraceStepDto | null): number | null {
  const v = step?.value;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() && Number.isFinite(Number(v))) return Number(v);
  return null;
}

function formatMoneyFromCents(cents: number): string {
  return fmt(cents / 100);
}

/** Trace step values stored as whole-dollar principal (not cents). */
const TRACE_DOLLAR_CODES = new Set([
  'LEGACY_INCOME',
  'GRID_RAW',
  'GRID_CAPPED',
  'LEGACY_PRA_LIMIT',
  'AFFORDABILITY_MAX',
  'MAX_ELIGIBLE',
  'CAPPED_TO_REQUESTED',
  'FEDERAL_AMOUNT_EXCEEDED',
  'MAXLOANAMT_CAP',
  'EI_CAP',
  'GENERAL_CAP',
  'AMOUNT_DUE_REDUCE',
]);

function formatMoneyDollars(value: number, code: string): string {
  if (code.endsWith('_CENTS')) {
    return formatMoneyFromCents(value);
  }
  if (TRACE_DOLLAR_CODES.has(code)) {
    return fmt(Math.round(value));
  }
  return fmt(value);
}

function formatTraceValue(code: string, value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string') return value.trim() || '—';
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (code === 'SCORING_TABLE') {
      const desc = SCORING_TABLE_DESCRIPTIONS[value];
      return desc ? `Table ${value} — ${desc}` : `Table ${value}`;
    }
    if (code === 'INCOME_RANGE') {
      const band = INCOME_RANGE_LABELS[value];
      return band ? `Band ${value} — ${band}` : `Band ${value}`;
    }
    if (code === 'LOAN_CATEGORY') {
      const tier = LOAN_CATEGORY_LABELS[value];
      return tier ? `Tier ${value} — ${tier}` : `Tier ${value}`;
    }
    if (code === 'DEBT_RATIO') {
      const pct = value <= 1 ? value * 100 : value;
      return `${pct.toFixed(0)}% of income`;
    }
    if (
      code === 'STACKING_REVIEW' ||
      code === 'STACKING_DECLINE' ||
      code === 'LOAN_VELOCITY' ||
      code === 'DEBT_NSF'
    ) {
      return String(value);
    }
    if (code.endsWith('_CENTS')) {
      return formatMoneyFromCents(value);
    }
    return formatMoneyDollars(value, code);
  }
  return String(value);
}

const ADJUDICATOR_TRACE_LABELS: Record<string, string> = {
  INCOME_CENTS: 'Verified bank income',
  LEGACY_INCOME: 'Income for pre-approval table',
  SCORING_TABLE: 'Scoring profile (table)',
  INCOME_RANGE: 'Income band',
  LOAN_CATEGORY: 'Repeat-customer tier',
  GRID_RAW: 'Table cell calculation',
  GRID_CAPPED: 'After cell maximum',
  GRID_MISS: 'No table cell found',
  MAXLOANAMT_CAP: '50% of income cap',
  EI_CAP: 'EI income cap',
  GENERAL_CAP: 'Product maximum cap',
  AMOUNT_DUE_REDUCE: 'Reduced by open balance',
  LEGACY_PRA_LIMIT: 'Pre-approval table limit',
  EXISTING_DEBT_CENTS: 'Existing loan payments (bank)',
  AVAILABLE_PAYMENT_CENTS: 'Payment room available',
  AFFORDABILITY_MAX: 'Bank affordability ceiling',
  MAX_ELIGIBLE: 'Offer before request cap',
  CAPPED_TO_REQUESTED: 'Capped to customer request',
  ELOD_UNAVAILABLE: 'Bank debt data missing',
  INTERNAL_C4Y: 'Cash4You loan on account',
  STACKING_DECLINE: 'Too many payday lenders',
  STACKING_REVIEW: 'Payday lender count (note)',
  DEBT_NSF: 'Returned loan payments (90d)',
  DEBT_RATIO: 'Debt vs income ratio',
  LOAN_VELOCITY: 'Recent loan deposits (90d)',
  BELOW_PRODUCT_MIN: 'Below $100 minimum',
  ZERO_INCOME: 'No verified income',
  JURISDICTION_BLOCKED: 'Province not eligible',
  FEDERAL_AMOUNT_EXCEEDED: 'Over federal max amount',
};

function humanizeTraceDetail(code: string, detail: string): string {
  const d = detail.trim();
  if (code === 'GRID_RAW' && /CEILING/i.test(d)) {
    return 'Table formula: income × loan %, rounded to nearest $10';
  }
  if (code === 'SCORING_TABLE') return 'From income type, direct deposit, and pay schedule (legacy trigger rules)';
  if (code === 'INCOME_RANGE') return 'Monthly income bucket used to pick the table row';
  if (code === 'LOAN_CATEGORY') return 'Prior payday count, adjusted if last loan repaid slowly';
  if (code === 'AVAILABLE_PAYMENT_CENTS') return 'Lower of 40% of income and 50% of income minus existing debt';
  if (code === 'AFFORDABILITY_MAX') return 'Payment room converted to principal ($100–$1,000)';
  if (code === 'LEGACY_PRA_LIMIT') return 'Final limit after table lookup and product caps';
  if (code === 'MAX_ELIGIBLE') return 'Minimum of pre-approval table limit and bank ceiling';
  return d || '—';
}

export function getPdlTraceLabel(code: string): string {
  return (
    ADJUDICATOR_TRACE_LABELS[code] ??
    code.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

export function buildPdlAdjudicatorTraceRows(
  pdl?: PdlAffordabilityJourneyDto | null,
): PdlAdjudicatorTraceRow[] {
  const trace = pdl?.affordabilityTrace ?? [];
  return trace.map((step) => ({
    code: step.code,
    phase: TRACE_PHASE[step.code] ?? 'other',
    label: ADJUDICATOR_TRACE_LABELS[step.code] ?? step.code.replace(/_/g, ' ').toLowerCase(),
    detail: humanizeTraceDetail(step.code, step.detail),
    value: formatTraceValue(step.code, step.value),
  }));
}

export function groupPdlAdjudicatorTraceRows(
  rows: PdlAdjudicatorTraceRow[],
): Array<{ phase: PdlTracePhase; label: string; rows: PdlAdjudicatorTraceRow[] }> {
  const order: PdlTracePhase[] = ['profile', 'pre_approval', 'bank', 'final', 'hard_stop', 'other'];
  const buckets = new Map<PdlTracePhase, PdlAdjudicatorTraceRow[]>();
  for (const row of rows) {
    const list = buckets.get(row.phase) ?? [];
    list.push(row);
    buckets.set(row.phase, list);
  }
  return order
    .filter((phase) => (buckets.get(phase)?.length ?? 0) > 0)
    .map((phase) => ({
      phase,
      label: TRACE_PHASE_LABELS[phase],
      rows: buckets.get(phase)!,
    }));
}

export type PdlMechanismSummary = {
  lines: string[];
  preApprovalLimit: number | null;
  bankCeiling: number | null;
  binding: 'legacy' | 'affordability' | 'request' | null;
};

export function buildPdlMechanismSummary(
  pdl?: PdlAffordabilityJourneyDto | null,
  maxEligible?: number | null,
): PdlMechanismSummary | null {
  const trace = pdl?.affordabilityTrace;
  if (!trace?.length) return null;

  const table = readTraceNumber(traceStep(trace, 'SCORING_TABLE'));
  const band = readTraceNumber(traceStep(trace, 'INCOME_RANGE'));
  const tier = readTraceNumber(traceStep(trace, 'LOAN_CATEGORY'));
  const legacyLimit = readTraceNumber(traceStep(trace, 'LEGACY_PRA_LIMIT'));
  const bankCeiling = readTraceNumber(traceStep(trace, 'AFFORDABILITY_MAX'));

  const lines: string[] = [];
  if (table != null) {
    const desc = SCORING_TABLE_DESCRIPTIONS[table];
    lines.push(desc ? `Scoring table ${table}: ${desc}` : `Scoring table ${table}`);
  }
  if (band != null && INCOME_RANGE_LABELS[band]) {
    lines.push(`Income band: ${INCOME_RANGE_LABELS[band]}`);
  }
  if (tier != null && LOAN_CATEGORY_LABELS[tier]) {
    lines.push(`Customer tier: ${LOAN_CATEGORY_LABELS[tier]}`);
  }
  if (legacyLimit != null) {
    lines.push(`Pre-approval table limit: ${fmt(legacyLimit)}`);
  }
  if (bankCeiling != null) {
    lines.push(`Bank payment-room ceiling: ${fmt(bankCeiling)}`);
  }

  if (lines.length === 0) return null;

  let binding: PdlMechanismSummary['binding'] = null;
  if (legacyLimit != null && bankCeiling != null && maxEligible != null) {
    if (maxEligible === legacyLimit && legacyLimit < bankCeiling) binding = 'legacy';
    else if (maxEligible === bankCeiling && bankCeiling < legacyLimit) binding = 'affordability';
  }

  return {
    lines,
    preApprovalLimit: legacyLimit,
    bankCeiling,
    binding,
  };
}

/** Replace backend jargon in remark / capacity messages for adjudicator surfaces. */
export function humanizePdlAdjudicatorMessage(
  message: string,
  context?: {
    requested?: number | null;
    maxEligible?: number | null;
    legacyLimit?: number | null;
    affordabilityMax?: number | null;
  },
): string {
  const text = message.trim();
  if (!text) return text;

  const legacyMatch = text.match(
    /legacy PRA grid\s*\(\$?([\d,]+)\)\s*rather than bank affordability\s*\(\$?([\d,]+)\)/i,
  );
  if (legacyMatch) {
    const legacy = Number(legacyMatch[1]!.replace(/,/g, ''));
    const bank = Number(legacyMatch[2]!.replace(/,/g, ''));
    const offer = context?.maxEligible ?? legacy;
    const req =
      context?.requested != null && context.requested > 0
        ? ` Customer requested ${fmt(context.requested)}.`
        : '';
    return (
      `Approved ${fmt(offer)} because the Cash4You pre-approval table limit (${fmt(legacy)}) ` +
      `is lower than what bank payment room allows (${fmt(bank)}).${req}`
    );
  }

  const affordMatch = text.match(
    /PTI\/TDS\/ELOD\s*\(\$?([\d,]+)\)\s*rather than legacy PRA grid\s*\(\$?([\d,]+)\)/i,
  );
  if (affordMatch) {
    const bank = Number(affordMatch[1]!.replace(/,/g, ''));
    const legacy = Number(affordMatch[2]!.replace(/,/g, ''));
    return (
      `Approved ${fmt(context?.maxEligible ?? bank)} because bank payment room (${fmt(bank)}) ` +
      `is lower than the pre-approval table limit (${fmt(legacy)}).`
    );
  }

  if (/legacy PRA grid limit/i.test(text)) {
    return text.replace(/legacy PRA grid limit/gi, 'Cash4You pre-approval table limit');
  }

  if (/^You requested/i.test(text)) {
    return text
      .replace(/^You requested/i, 'Customer requested')
      .replace(/\bYour requested\b/i, 'Customer requested');
  }

  return text
    .replace(/\blegacy PRA grid\b/gi, 'Cash4You pre-approval table')
    .replace(/\bPRA grid\b/gi, 'pre-approval table');
}

export function humanizePdlBindingWhy(
  rawWhy: string | null | undefined,
  context: {
    requested?: number | null;
    maxEligible?: number | null;
    legacyLimit?: number | null;
    affordabilityMax?: number | null;
  },
): string | null {
  if (!rawWhy?.trim()) return null;
  return humanizePdlAdjudicatorMessage(rawWhy, context);
}
